import { useEffect, useState, useCallback, useRef } from "react";
import { DragDropContext } from "@hello-pangea/dnd";
import { api, apiError } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { useFilters } from "@/hooks/useFilters";
import PageHeader from "@/components/common/PageHeader";
import FilterBar from "@/components/common/FilterBar";
import { Loading, ErrorState } from "@/components/common/States";
import StageColumn from "@/components/pipeline/StageColumn";
import StageMoveDialog from "@/components/pipeline/StageMoveDialog";
import DealDialog from "@/components/pipeline/DealDialog";
import { missingFields } from "@/components/pipeline/StageFieldsForm";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GitBranch, ChevronLeft, ChevronRight } from "lucide-react";

function moveCard(columns, dealId, from, to, toIndex) {
  const next = { ...columns, [from]: [...(columns[from] || [])] };
  if (to !== from) next[to] = [...(columns[to] || [])];
  const idx = next[from].findIndex((d) => d.id === dealId);
  if (idx === -1) return columns;
  const [card] = next[from].splice(idx, 1);
  next[to].splice(Math.min(toIndex ?? 0, next[to].length), 0, { ...card, stage: to });
  return next;
}

export default function Pipeline() {
  const meta = useMeta();
  const { filters, setFilters, apiParams } = useFilters();
  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [pending, setPending] = useState(null);
  const scrollRef = useRef(null);
  const boardRef = useRef(null);
  boardRef.current = board;

  const load = useCallback(async () => {
    setError("");
    try { const { data } = await api.get("/deals/board", { params: apiParams }); setBoard(data); }
    catch (e) { setError(apiError(e)); }
  }, [apiParams]);
  useEffect(() => { load(); }, [load]);

  // Optimistic move; reverts the board if the API rejects it.
  const commitMove = useCallback(async (deal, from, to, fields, toIndex = 0) => {
    const snapshot = boardRef.current.columns;
    setBoard((b) => ({ ...b, columns: moveCard(b.columns, deal.id, from, to, toIndex) }));
    try {
      const { data } = await api.post(`/deals/${deal.id}/stage`, { stage: to, fields });
      setBoard((b) => ({ ...b, columns: { ...b.columns, [to]: b.columns[to].map((d) => (d.id === deal.id ? data : d)) } }));
      toast.success(`Moved to ${to}`);
      return true;
    } catch (e) {
      setBoard((b) => ({ ...b, columns: snapshot }));
      toast.error(apiError(e));
      return false;
    }
  }, []);

  const onDragEnd = ({ source, destination, draggableId }) => {
    if (!destination || destination.droppableId === source.droppableId) return;
    const from = source.droppableId, to = destination.droppableId;
    const deal = board.columns[from].find((d) => d.id === draggableId);
    if (!deal) return;
    if (missingFields(board.stage_fields[to], deal.stage_fields || {}).length) {
      setPending({ deal, from, to, toIndex: destination.index });
      return;
    }
    commitMove(deal, from, to, {}, destination.index);
  };

  const confirmPending = async (fields) => {
    const p = pending;
    const ok = await commitMove(p.deal, p.from, p.to, fields, p.toIndex);
    if (ok) setPending(null);
    return ok;
  };

  const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir * 600, behavior: "smooth" });

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!board) return <Loading label="Loading pipeline…" />;

  const totalDeals = board.stages.reduce((s, st) => s + (board.columns[st]?.length || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Pipeline" subtitle={board.can_move ? "Drag a deal to another stage, or tap it to update details." : "View-only pipeline for your deals."} testid="pipeline-header"
        actions={
          <div className="hidden md:flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => scroll(-1)} data-testid="pipeline-scroll-left"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => scroll(1)} data-testid="pipeline-scroll-right"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        } />
      <FilterBar filters={filters} setFilters={setFilters} testid="pipeline-filter-bar" />

      {totalDeals === 0 && (
        <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-10 text-center" data-testid="pipeline-empty">
          <GitBranch className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No open deals for these filters. Convert a lead to create a deal.</p>
        </div>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <div ref={scrollRef} className="overflow-x-auto pb-4 medoho-scroll" data-testid="pipeline-scroll">
          <div className="flex gap-4 min-w-max items-stretch" data-testid="pipeline-board">
            {board.stages.map((st) => (
              <StageColumn key={st} stage={st} cards={board.columns[st] || []} canMove={board.can_move}
                requiredCount={(board.stage_fields[st] || []).filter((f) => f.required).length} onOpen={setSelected} />
            ))}
          </div>
        </div>
      </DragDropContext>

      <StageMoveDialog pending={pending} fields={pending ? board.stage_fields[pending.to] || [] : []}
        onCancel={() => { setPending(null); toast.info("Move cancelled — deal kept in its stage"); }} onConfirm={confirmPending} />
      <DealDialog deal={selected} stages={board.stages} stageFields={board.stage_fields} outcomes={meta.lost_outcomes || []}
        canEdit={board.can_move} onClose={() => setSelected(null)} onMove={commitMove} onChanged={load} />
    </div>
  );
}
